/**
 * Statistikken i systemadminpanelet (FF-12, #180): totalkort øverst og en
 * sorterbar tabell med én rad per familie, for et vindu på 7 dager, 30 dager,
 * inneværende måned eller egendefinert — hele dager i Europe/Oslo.
 *
 * Bare tall (D3): API-et gir antall, summer og tider, aldri innhold, og
 * medlemmer telles uten å navngis. Ingen grafer ennå: det finnes ingen
 * tidsserie å tegne før daglige snapshots eventuelt kommer.
 */
import { useId, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { SYSTEM_STATS_MAX_DAYS, systemStatsDays, type SystemStatsFamily } from '@stell/shared';
import { Card, SectionLabel, SegmentedTabs } from '../../components/primitives';
import { GLASS_SURFACE } from '../../components/primitives/Card';
import { osloToday } from '../../lib/osloToday';
import { SYSTEM_STATS_KEY, SystemApi } from './systemApi';

type Period = '7d' | '30d' | 'month' | 'custom';

/** `date` flyttet `days` kalenderdager (middagsanker, uavhengig av sommertid). */
function shiftDate(date: string, days: number): string {
  const anchor = new Date(`${date}T12:00:00Z`);
  anchor.setUTCDate(anchor.getUTCDate() + days);
  return anchor.toISOString().slice(0, 10);
}

function presetWindow(period: Exclude<Period, 'custom'>, today: string): { since: string; until: string } {
  if (period === 'month') return { since: `${today.slice(0, 8)}01`, until: today };
  return { since: shiftDate(today, period === '7d' ? -6 : -29), until: today };
}

const int = new Intl.NumberFormat('nb-NO');
const usd = new Intl.NumberFormat('nb-NO', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

function lastActive(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' }) : '—';
}

/** Kolonnene i tabellen. `name` sorteres alfabetisk, resten som tall/tid. */
const COLUMNS = [
  'name',
  'active_members',
  'ai_turns',
  'ai_cost_usd',
  'messages',
  'chores_completed',
  'events_created',
  'shopping_added',
  'meals_planned',
  'points_awarded',
  'last_active_at',
] as const;
type Column = (typeof COLUMNS)[number];

function compare(a: SystemStatsFamily, b: SystemStatsFamily, col: Column): number {
  if (col === 'name') return a.name.localeCompare(b.name, 'nb');
  if (col === 'last_active_at') return (a.last_active_at ?? '').localeCompare(b.last_active_at ?? '');
  return a[col] - b[col];
}

function cell(f: SystemStatsFamily, col: Exclude<Column, 'name'>): string {
  if (col === 'ai_cost_usd') return usd.format(f.ai_cost_usd);
  if (col === 'last_active_at') return lastActive(f.last_active_at);
  if (col === 'active_members') return `${int.format(f.active_members)} / ${int.format(f.members)}`;
  return int.format(f[col]);
}

function DateField({ label, value, onChange, testId }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  testId: string;
}): ReactElement {
  const id = useId();
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
      <SectionLabel as="label" htmlFor={id} flush>
        {label}
      </SectionLabel>
      <input id={id} type="date" data-testid={testId} className="field" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

export function SystemStatsPanel(): ReactElement {
  const { t } = useTranslation();
  const today = osloToday();
  const [period, setPeriod] = useState<Period>('30d');
  const [custom, setCustom] = useState(() => presetWindow('30d', today));
  const [sort, setSort] = useState<{ col: Column; desc: boolean }>({ col: 'ai_cost_usd', desc: true });

  const range = period === 'custom' ? custom : presetWindow(period, today);
  const valid =
    /^\d{4}-\d{2}-\d{2}$/.test(range.since) &&
    /^\d{4}-\d{2}-\d{2}$/.test(range.until) &&
    range.since <= range.until &&
    systemStatsDays(range.since, range.until) <= SYSTEM_STATS_MAX_DAYS;

  const stats = useQuery({
    queryKey: [...SYSTEM_STATS_KEY, range.since, range.until],
    queryFn: () => SystemApi.stats(range.since, range.until),
    enabled: valid,
  });

  const rows = useMemo(() => {
    const list = [...(stats.data?.families ?? [])];
    list.sort((a, b) => compare(a, b, sort.col) * (sort.desc ? -1 : 1));
    return list;
  }, [stats.data, sort]);

  const totals = stats.data?.totals;
  const tiles: Array<[string, string]> = totals
    ? [
        ['families', int.format(totals.families)],
        ['active_members', `${int.format(totals.active_members)} / ${int.format(totals.members)}`],
        ['ai_cost_usd', usd.format(totals.ai_cost_usd)],
        ['ai_turns', int.format(totals.ai_turns)],
        ['messages', int.format(totals.messages)],
        ['chores_completed', int.format(totals.chores_completed)],
        ['events_created', int.format(totals.events_created)],
        ['shopping_added', int.format(totals.shopping_added)],
        ['meals_planned', int.format(totals.meals_planned)],
        ['points_awarded', int.format(totals.points_awarded)],
        ['devices', int.format(totals.devices)],
        ['chore_templates', int.format(totals.chore_templates)],
      ]
    : [];

  function toggleSort(col: Column): void {
    setSort((s) => (s.col === col ? { col, desc: !s.desc } : { col, desc: col !== 'name' }));
  }

  return (
    <section data-testid="system-stats" className="flex flex-col gap-4">
      <h2 className="m-0 text-heading-m font-extrabold text-ink">{t('system.stats.title')}</h2>
      <SegmentedTabs
        label={t('system.stats.period_label')}
        dense
        value={period}
        onChange={setPeriod}
        options={(['7d', '30d', 'month', 'custom'] as const).map((p) => ({
          value: p,
          label: t(`system.stats.period.${p}`),
          testId: `system-stats-period-${p}`,
        }))}
      />
      {period === 'custom' ? (
        <div className="flex flex-wrap gap-3">
          <DateField
            label={t('system.stats.since')}
            value={custom.since}
            testId="system-stats-since"
            onChange={(since) => setCustom((c) => ({ ...c, since }))}
          />
          <DateField
            label={t('system.stats.until')}
            value={custom.until}
            testId="system-stats-until"
            onChange={(until) => setCustom((c) => ({ ...c, until }))}
          />
        </div>
      ) : null}

      {!valid ? (
        <p data-testid="system-stats-invalid" role="alert" className="m-0 text-sm text-bad">
          {t('system.stats.invalid_window', { max: SYSTEM_STATS_MAX_DAYS })}
        </p>
      ) : stats.isPending ? (
        <p className="m-0 text-sm text-ink-2">{t('system.loading')}</p>
      ) : stats.isError ? (
        <p role="alert" className="m-0 text-sm text-bad">{t('system.errors.generic')}</p>
      ) : (
        <>
          <ul data-testid="system-stats-totals" className="m-0 grid list-none grid-cols-2 gap-3 p-0 sm:grid-cols-4">
            {tiles.map(([key, value]) => (
              <li key={key}>
                <Card variant="soft" className="flex h-full flex-col gap-1">
                  <span className="text-sm text-ink-2">{t(`system.stats.metric.${key}`)}</span>
                  <span data-testid={`system-stats-total-${key}`} className="text-heading-m font-extrabold text-ink tnum">
                    {value}
                  </span>
                </Card>
              </li>
            ))}
          </ul>

          {totals && totals.ai_by_model.length > 0 ? (
            <Card variant="soft">
              <SectionLabel as="h3" flush>{t('system.stats.by_model')}</SectionLabel>
              <ul data-testid="system-stats-models" className="m-0 mt-2 flex list-none flex-col gap-1 p-0 text-sm">
                {totals.ai_by_model.map((m) => (
                  <li key={m.model} className="flex flex-wrap justify-between gap-x-4 text-ink">
                    <span className="font-bold">{m.model}</span>
                    <span className="text-ink-2 tnum">
                      {t('system.stats.model_line', {
                        turns: int.format(m.turns),
                        input: int.format(m.input_tokens),
                        output: int.format(m.output_tokens),
                        cost: usd.format(m.cost_usd),
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          <div className={`${GLASS_SURFACE} overflow-x-auto rounded-lg`}>
            <table data-testid="system-stats-table" className="w-full border-collapse text-sm">
              <caption className="sr-only">{t('system.stats.table_caption')}</caption>
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th
                      key={col}
                      scope="col"
                      aria-sort={sort.col === col ? (sort.desc ? 'descending' : 'ascending') : 'none'}
                      className={`border-b border-card-line px-3 py-2 font-bold text-ink-2 ${col === 'name' ? 'text-left' : 'text-right'}`}
                    >
                      <button
                        type="button"
                        data-testid={`system-stats-sort-${col}`}
                        className="min-h-11 whitespace-nowrap font-bold"
                        onClick={() => toggleSort(col)}
                      >
                        {t(`system.stats.column.${col}`)}
                        {sort.col === col ? (sort.desc ? ' ↓' : ' ↑') : ''}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((f) => (
                  <tr key={f.id} data-testid={`system-stats-row-${f.code}`} className="border-b border-card-line last:border-b-0">
                    <th scope="row" className="px-3 py-2 text-left font-bold text-ink">
                      <span className="block whitespace-nowrap">{f.name}</span>
                      <span className="block text-ink-2 font-normal tnum">{f.code}</span>
                    </th>
                    {COLUMNS.slice(1).map((col) => (
                      <td key={col} className="whitespace-nowrap px-3 py-2 text-right text-ink tnum">
                        {cell(f, col as Exclude<Column, 'name'>)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="m-0 text-sm text-ink-2">{t('system.stats.footnote')}</p>
        </>
      )}
    </section>
  );
}
